import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { LoggerProvider } from '@shared';
import { IGridRequest, IGridDataSourceResponse, GridSourceFunction } from './grid-request.model';
import { GridBaseQuery } from './grid-base-query.model';

export type { IGridRequest, IGridDataSourceResponse, GridSourceFunction } from './grid-request.model';
export type { GridBaseQuery, PaginatedResult } from './grid-base-query.model';

@Injectable({
  providedIn: 'root',
})
export class GridHelperService {
  private readonly _logger = inject(LoggerProvider).createScoped('GridHelper');

  public createGridSource = (fn: GridSourceFunction, param: unknown = null) => {
    return (request: IGridRequest) => {
      return {
        call: () => this.callGridSource(fn, request, param),
      };
    };
  };

  /**
   * Build standard HttpParams for a server-side paged request.
   * Handles Skip/Take/Sort/Filter and an optional search term.
   */
  public buildGridParams(query: GridBaseQuery): HttpParams {
    // Calculate Skip - prefer direct skip value, fallback to page-based calculation
    const skip = query.gridRequest.skip !== undefined
      ? query.gridRequest.skip
      : (((query.gridRequest.page || 1) - 1) * (query.gridRequest.pageSize || 10));

    // Calculate Take - prefer direct take value, fallback to pageSize
    const take = query.gridRequest.take !== undefined
      ? query.gridRequest.take
      : (query.gridRequest.pageSize || 10);

    let params = new HttpParams()
      .set('Skip', skip.toString())
      .set('Take', take.toString());

    if (query.gridRequest.sort) {
      params = params.set('Sort', query.gridRequest.sort);
    }

    if (query.gridRequest.filter) {
      params = params.set('Filter', query.gridRequest.filter);
    }

    if (query.searchTerm) {
      params = params.set('search', query.searchTerm);
    }

    return params;
  }

  private callGridSource = (
    fn: GridSourceFunction,
    request: IGridRequest,
    param: unknown = null
  ): Promise<{ data: unknown[]; total: number }> => {
    return firstValueFrom(fn(request, param))
      .then((response: IGridDataSourceResponse | undefined) => {
        return {
          data: response?.data || [],
          total: response?.totalElements || 0,
        };
      })
      .catch((error) => {
        this._logger.error('Grid source error', error);
        return {
          data: [],
          total: 0,
        };
      });
  };
}
